import { cn } from '@/lib/ui/utils';
import type { ElementType, ReactNode } from 'react';

type CmsTitleGroupSize = 'hero' | 'section';
type CmsTitleGroupAlign = 'left' | 'center';
type CmsTitleGroupTone = 'lead' | 'body' | 'body-sm';

const titleClassMap: Record<CmsTitleGroupSize, string> = {
  hero: 'cms-title-hero',
  section: 'cms-title-section',
};

const descriptionClassMap: Record<CmsTitleGroupTone, string> = {
  lead: 'cms-body-lead',
  body: 'cms-body',
  'body-sm': 'cms-body-sm',
};

type CmsTitleGroupProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  as?: ElementType;
  size?: CmsTitleGroupSize;
  align?: CmsTitleGroupAlign;
  descriptionTone?: CmsTitleGroupTone;
  className?: string;
  eyebrowClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  actionsClassName?: string;
};

export function CmsTitleGroup({
  eyebrow,
  title,
  description,
  actions,
  as,
  size,
  align = 'left',
  descriptionTone = 'lead',
  className,
  eyebrowClassName,
  titleClassName,
  descriptionClassName,
  actionsClassName,
}: CmsTitleGroupProps) {
  const resolvedSize = size ?? (as === 'h1' ? 'hero' : 'section');
  const TitleTag = (as ??
    (resolvedSize === 'hero' ? 'h1' : 'h2')) as ElementType;

  return (
    <div
      className={cn(
        'cms-width-guard',
        'cms-title-group',
        align === 'center' && 'cms-title-group-center',
        resolvedSize === 'hero' && 'cms-title-group-hero',
        className
      )}
    >
      {eyebrow ? (
        <p
          className={cn(
            'cms-eyebrow cms-title-group-eyebrow',
            eyebrowClassName
          )}
        >
          {eyebrow}
        </p>
      ) : null}

      <div className="cms-width-guard cms-title-group-copy">
        <TitleTag
          className={cn(
            'cms-copy-guard',
            titleClassMap[resolvedSize],
            titleClassName
          )}
        >
          {title}
        </TitleTag>

        {description ? (
          <p
            className={cn(
              'cms-copy-guard',
              'cms-title-group-description',
              descriptionClassMap[descriptionTone],
              descriptionClassName
            )}
          >
            {description}
          </p>
        ) : null}
      </div>

      {actions ? (
        <div
          className={cn(
            'cms-width-guard cms-title-group-actions',
            actionsClassName
          )}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}
